import Link from "next/link";
import { NextSeo } from "next-seo";
import { ArrowRight } from "lucide-react";
import Footer from "@/components/shared/Footer";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import CalculatorWidget from "./CalculatorWidget";
import CalculatorCard from "./CalculatorCard";
import CalcCtaBand from "./CalcCtaBand";
import { getOtherCalculators } from "@/lib/calculators/data";
import type { Calculator } from "@/lib/calculators/types";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";

export default function CalculatorLayout({ calculator }: { calculator: Calculator }) {
  const { slug, title, description, eyebrow, seoTitle, seoDescription, icon: Icon } = calculator;
  const others = getOtherCalculators(slug);
  const path = `/calculators/${slug}`;
  const crumbs = breadcrumbsForPath(path);

  return (
    <>
      <NextSeo title={`${seoTitle} | Crayo`} description={seoDescription} />
      <JsonLd
        nodes={pageGraph({
          path,
          name: seoTitle,
          description: seoDescription,
          crumbs,
          faqs: calculator.faqs,
        })}
      />

      <article className="w-full overflow-x-clip bg-white">
        <section className="border-b border-[#E7ECF6] bg-white">
          <div className="mx-auto w-full max-w-screen-xl px-5 pb-10 pt-10 sm:px-6 md:px-12 md:pb-12 md:pt-14 lg:px-20">
            <Breadcrumbs items={crumbs} className="mb-6 max-w-none px-0 pt-0 md:px-0 lg:px-0" />

            <div className="flex max-w-[760px] flex-col items-start gap-4">
              <span className="inline-flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-crayoBlue">
                <Icon className="size-3.5" strokeWidth={2.25} />
                {eyebrow}
              </span>
              <h1 className="text-balance text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
                {title}
              </h1>
              <p className="max-w-[620px] text-[15px] leading-[1.6] text-[#5C6678] sm:text-[17px]">
                {description}
              </p>
            </div>

            <div className="mt-8">
              <CalculatorWidget key={calculator.slug} calculator={calculator} />
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto w-full max-w-screen-xl px-5 py-12 sm:px-6 md:px-12 md:py-16 lg:px-20 lg:py-20">
            <div className="grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16">
              <div className="min-w-0 max-w-[720px]">
                <h2 className="text-[22px] font-semibold leading-[1.2] tracking-tight text-gray-900 sm:text-[26px]">
                  {calculator.bodyHeading}
                </h2>
                <div className="mt-5 flex flex-col gap-4 text-[15px] leading-[1.7] text-[#41485A] sm:text-[16px]">
                  {calculator.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                <h2 className="mt-12 text-[22px] font-semibold leading-[1.2] tracking-tight text-gray-900 sm:text-[26px]">
                  Frequently asked questions
                </h2>
                <dl className="mt-5 flex flex-col divide-y divide-[#E7ECF6] border-t border-[#E7ECF6]">
                  {calculator.faqs.map((faq) => (
                    <div key={faq.question} className="py-5">
                      <dt className="text-[16px] font-semibold text-gray-900">{faq.question}</dt>
                      <dd className="mt-2 text-[14.5px] leading-[1.7] text-[#41485A]">
                        {faq.answer}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <aside className="lg:sticky lg:top-24 lg:self-start">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
                  More calculators
                </span>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                  {others.slice(0, 3).map((c) => (
                    <CalculatorCard key={c.slug} calculator={c} />
                  ))}
                </div>
                <Link
                  href="/calculators"
                  prefetch={false}
                  className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#E7ECF6] px-4 py-3 text-[13px] font-semibold text-crayoBlue transition-colors hover:border-crayoBlue/40 hover:bg-crayoBlue/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crayoBlue/40"
                >
                  View all calculators
                  <ArrowRight className="size-3.5" strokeWidth={2.25} aria-hidden />
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <RelatedPages path={path} />

        <CalcCtaBand />
      </article>

      <Footer />
    </>
  );
}
